// TanStack Query 5 client per ADR-013 §8. Server state lives here; UI
// state lives in Zustand (src/store/ui.ts). Keep the boundary clean —
// anything derived from a backend response goes through Query, never
// Zustand.
//
// Operator console reuses the customer-web tuning: 30s stale window,
// Spec 58 envelope-driven retry plus bounded jittered delay for safe GETs,
// and no automatic mutation retry (every retry must be explicit +
// idempotent per ADR-002).

import { QueryClient } from "@tanstack/react-query";

import { EnvelopeError } from "./envelope";

const MAX_AUTOMATIC_RETRIES = 2;
const BASE_RETRY_DELAY_MS = 1_000;
const MAX_RETRY_DELAY_MS = 30_000;
const JITTER_RATIO = 0.1;
const MAX_JITTER_MS = 1_000;
const MAX_NATIVE_TIMER_DELAY_MS = 2 ** 31 - 1;
const MAX_AUTOMATIC_RETRY_AFTER_MS = MAX_NATIVE_TIMER_DELAY_MS - MAX_JITTER_MS;

function isValidRetryAfterMs(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    Number.isInteger(value) &&
    value >= 0
  );
}

/**
 * Adds strictly positive, bounded jitter without ever retrying before a
 * server-provided Retry-After hint. The 10%/1s cap avoids synchronized retry
 * bursts without turning a short bounded retry policy into a long tail.
 */
function positiveBoundedJitter(delayMs: number): number {
  const maxJitterMs = Math.min(
    Math.max(1, Math.ceil(delayMs * JITTER_RATIO)),
    MAX_JITTER_MS,
  );
  return Math.max(1, Math.ceil(Math.random() * maxJitterMs));
}

/**
 * Spec 58 retryability, from the envelope's own classification-derived
 * `retryable` flag — never a message-string pattern match. `EnvelopeError.
 * message` is `${code}: ${message}` for the common (envelope-with-errors[])
 * case, which never contains the raw HTTP status, so a `/4\d\d/`-style
 * regex over the message silently fails to match and falls through to
 * retrying — wasting 2 retries + backoff on a 4xx (validation/not_found/
 * conflict) that will never succeed. Non-envelope throwables (e.g. a raw
 * network TypeError before a response is even parsed) keep the previous
 * failureCount-only behavior. Mirrors customer-web's shared EnvelopeError
 * predicate (frontend-audit LOW — same EnvelopeError shape, same live bug).
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof EnvelopeError) {
    // A server hint plus its maximum jitter must fit native timers. Do not
    // clamp an oversized hint: that would retry before the server allowed.
    // This predicate prevents TanStack Query from scheduling that retry.
    const retryAfterMs = error.retryAfterMs;
    const hasOversizedRetryHint =
      isValidRetryAfterMs(retryAfterMs) &&
      retryAfterMs > MAX_AUTOMATIC_RETRY_AFTER_MS;
    return (
      error.retryable &&
      !hasOversizedRetryHint &&
      failureCount < MAX_AUTOMATIC_RETRIES
    );
  }
  return failureCount < MAX_AUTOMATIC_RETRIES;
}

/**
 * Shared safe-query retry delay. A retryable envelope's finite, non-negative
 * integer `retry_after_ms` is authoritative: only positive jitter is added,
 * so the client never retries before the server's requested limit. Oversized
 * hints are rejected by shouldRetryQuery rather than being shortened here.
 * Without a usable hint (including raw network errors), use capped exponential
 * backoff with the same bounded positive jitter.
 */
export function retryDelay(failureCount: number, error: unknown): number {
  const retryAfterMs =
    error instanceof EnvelopeError && error.retryable
      ? error.retryAfterMs
      : undefined;

  if (isValidRetryAfterMs(retryAfterMs)) {
    return retryAfterMs + positiveBoundedJitter(retryAfterMs);
  }

  const backoffMs = Math.min(
    BASE_RETRY_DELAY_MS * 2 ** failureCount,
    MAX_RETRY_DELAY_MS,
  );
  return backoffMs + positiveBoundedJitter(backoffMs);
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 30s stale window — operator dashboards are mostly read-heavy;
      // hot endpoints (incident feed, SLO burn) override per-query.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      // Off by default: focus refetch storms the API on tab switches.
      // Live surfaces opt in per-query (useOperatorMe keeps its explicit true).
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery,
      retryDelay,
    },
    mutations: {
      // No automatic mutation retry — every retry must be explicit +
      // idempotent. Plan madde 7 hardens this with idempotency-key
      // headers on operator mutations (refunds, plan overrides).
      retry: false,
    },
  },
});
